// Pure helpers for the Alerts incident-history view (Phase 1).
// Query building, duration formatting, per-type facts, route-type detection.
// No React/fetch -> unit-testable. Cursor is opaque here (server-owned).

export const HISTORY_ROUTE_TYPES = new Set(["off_route", "toll_overage", "eta_drift"]);

export const TYPE_LABELS = {
  eta_drift: "ETA drift", pto_conflict: "PTO conflict", returning_driver_available: "Driver available",
  hos_violation: "HOS violation", hos_violation_daily: "HOS violation", off_route: "Off route",
  toll_overage: "Toll overage", fuel_deviation: "Fuel deviation", motive_sync_stale: "Motive sync stale",
  samsara_sync_stale: "Samsara sync stale", pickup_overdue: "Pickup overdue", pickup_risk: "Pickup risk",
  delivery_risk: "Delivery risk", possible_missed_pickup: "Possible missed pickup",
  possible_missed_delivery: "Possible missed delivery",
};

export function typeLabel(t) {
  return TYPE_LABELS[t] || (t ? t.replace(/_/g, " ") : "Alert");
}

export function isRouteType(t) {
  return HISTORY_ROUTE_TYPES.has(t);
}

// Build the query string for GET /fleet/alert-history. statusTab is
// "resolved" | "all" (OPEN uses the existing /fleet/alerts endpoint, not this).
// Empty filters are omitted; cursor is passed opaquely for "Load more".
export function buildHistoryQuery(statusTab, filters = {}, cursor = null) {
  const p = new URLSearchParams();
  p.set("status", statusTab === "all" ? "all" : "resolved");
  const map = { severity: "severity", type: "alert_type", dispatcher: "dispatcher_id",
                truck: "truck_id", q: "q", from: "from", to: "to" };
  for (const [k, param] of Object.entries(map)) {
    const v = filters[k];
    if (v != null && String(v).trim() !== "") p.set(param, String(v).trim());
  }
  if (cursor) p.set("cursor", cursor);
  return p.toString();
}

export function formatDuration(seconds) {
  if (seconds == null || Number.isNaN(seconds)) return "";
  const s = Math.max(0, Math.round(seconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// One short, truthful per-type fact for the incident row (defensive about
// which fields/metadata exist on older rows). Falls back to a cost figure or
// nothing -- never fabricates.
export function keyFact(inc) {
  const m = inc.metadata || {};
  switch (inc.alert_type) {
    case "possible_missed_pickup":
    case "possible_missed_delivery": {
      const parts = [];
      if (m.confidence) parts.push(m.confidence);
      if (m.dwell_minutes != null) parts.push(`dwelled ${formatDuration(m.dwell_minutes * 60)}`);
      if (m.current_distance_miles != null) parts.push(`now ${Math.round(m.current_distance_miles)}mi away`);
      return parts.join(" · ");
    }
    case "toll_overage":
      return inc.verified_cost_impact != null
        ? `${inc.verified_cost_impact < 0 ? "under" : "over"} plan $${Math.abs(inc.verified_cost_impact).toFixed(2)}`
        : "";
    case "off_route":
      return m.distance_from_route_miles != null ? `${Math.round(m.distance_from_route_miles)} mi off route` : "";
    case "eta_drift":
      return m.drift_hours != null ? `${Math.abs(m.drift_hours).toFixed(1)}h ${m.drift_hours > 0 ? "late" : "early"}` : "";
    case "pickup_overdue":
      return m.overdue_minutes != null ? `${formatDuration(m.overdue_minutes * 60)} overdue` : "";
    default:
      if (inc.estimated_cost_impact != null) return `est. exposure $${Math.abs(inc.estimated_cost_impact).toFixed(2)}`;
      return "";
  }
}

// "Resolved incidents · 08/23/2026 – 09/22/2026" — makes the active window
// explicit so users understand RESOLVED is bounded.
export function windowLabel(statusTab, window) {
  if (!window || !window.from || !window.to) return "";
  const d = (s) => new Date(s.endsWith?.("Z") ? s : `${s}Z`).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
  const noun = statusTab === "all" ? "Incidents" : "Resolved incidents";
  return `${noun} · ${d(window.from)} – ${d(window.to)}`;
}
